(function () {
  if (window.BannannaHomeInteractions) {
    window.BannannaHomeInteractions.initAll();
    return;
  }

  function getPanelName(panel, fallback) {
    return panel.dataset.fitOutcomeName || fallback || 'Selected fit';
  }

  function decodePanelImages(panel) {
    if (!panel) return Promise.resolve();

    const images = Array.from(panel.querySelectorAll('img')).filter((image) => !image.complete || !image.naturalWidth);
    if (!images.length) return Promise.resolve();

    const decodeTasks = images.map((image) => {
      if (typeof image.decode === 'function') {
        return image.decode().catch(() => {});
      }

      return new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    });

    return Promise.race([
      Promise.all(decodeTasks),
      new Promise((resolve) => setTimeout(resolve, 160))
    ]);
  }

  function setFocusableState(root, isActive) {
    const focusable = root.querySelectorAll('a, button, input, textarea, select, summary, [tabindex]');
    root.toggleAttribute('inert', !isActive);

    focusable.forEach((element) => {
      if (!isActive) {
        if (!element.hasAttribute('data-bnn-original-tabindex')) {
          element.setAttribute('data-bnn-original-tabindex', element.getAttribute('tabindex') || '');
        }
        element.tabIndex = -1;
        return;
      }

      if (!element.hasAttribute('data-bnn-original-tabindex')) return;

      const originalTabIndex = element.getAttribute('data-bnn-original-tabindex');
      if (originalTabIndex === '') {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', originalTabIndex);
      }
      element.removeAttribute('data-bnn-original-tabindex');
    });
  }

  function initFitDiscovery(root) {
    if (!root || root.dataset.bannannaFitReady === 'true') return;

    const triggers = Array.from(root.querySelectorAll('[data-fit-trigger]'));
    const items = Array.from(root.querySelectorAll('[data-fit-item]'));
    const panels = Array.from(root.querySelectorAll('[data-fit-panel]'));
    const details = Array.from(root.querySelectorAll('[data-fit-detail]'));
    const status = root.querySelector('[data-fit-status]');

    if (!triggers.length) return;
    let visualSwapToken = 0;

    function getSafeIndex(index) {
      const requestedIndex = String(index || '0');
      const hasMatch = triggers.some((trigger) => trigger.dataset.fitTrigger === requestedIndex);
      return hasMatch ? requestedIndex : triggers[0].dataset.fitTrigger;
    }

    function broadcastFitChange(index) {
      document.dispatchEvent(new CustomEvent('bannanna:fit-change', { detail: { index } }));
    }

    function applyVisualPanelState(activeIndex) {
      panels.forEach((panel) => {
        const isActive = panel.dataset.fitPanel === activeIndex;
        panel.hidden = false;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    function setActiveVisualPanel(activeIndex, options) {
      const settings = Object.assign({ instant: false }, options);
      const nextPanel = panels.find((panel) => panel.dataset.fitPanel === activeIndex);
      const swapToken = ++visualSwapToken;

      if (settings.instant || !root.classList.contains('is-motion-ready')) {
        applyVisualPanelState(activeIndex);
        return;
      }

      decodePanelImages(nextPanel).then(() => {
        if (swapToken !== visualSwapToken) return;
        applyVisualPanelState(activeIndex);
      });
    }

    function setActive(index, options) {
      const settings = Object.assign({ broadcast: true, focus: false, instant: false }, options);
      const activeIndex = getSafeIndex(index);
      let activeTrigger = null;

      items.forEach((item) => {
        item.classList.toggle('is-active', item.dataset.fitItem === activeIndex);
      });

      triggers.forEach((trigger) => {
        const isActive = trigger.dataset.fitTrigger === activeIndex;
        if (isActive) activeTrigger = trigger;
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        trigger.tabIndex = isActive ? 0 : -1;
      });

      setActiveVisualPanel(activeIndex, { instant: settings.instant });

      details.forEach((detail) => {
        const isActive = detail.dataset.fitDetail === activeIndex;
        detail.classList.toggle('is-active', isActive);
        detail.tabIndex = isActive ? 0 : -1;
        detail.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        setFocusableState(detail, isActive);
      });

      if (status && activeTrigger) {
        const fitName = activeTrigger.textContent.trim();
        status.textContent = `${fitName} fit selected.`;
      }

      if (settings.focus && activeTrigger) activeTrigger.focus();
      if (settings.broadcast) broadcastFitChange(activeIndex);
    }

    function getTriggerIndex(trigger) {
      return triggers.indexOf(trigger);
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => setActive(trigger.dataset.fitTrigger));
      trigger.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        const triggerIndex = getTriggerIndex(trigger);
        const lastIndex = triggers.length - 1;
        let nextIndex = triggerIndex;

        if (event.key === 'ArrowLeft') nextIndex = triggerIndex === 0 ? lastIndex : triggerIndex - 1;
        if (event.key === 'ArrowRight') nextIndex = triggerIndex === lastIndex ? 0 : triggerIndex + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = lastIndex;

        setActive(triggers[nextIndex].dataset.fitTrigger, { focus: true });
      });
    });

    const activeTrigger = triggers.find((trigger) => trigger.classList.contains('is-active')) || triggers[0];
    setActive(activeTrigger.dataset.fitTrigger, { broadcast: false, instant: true });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.add('is-motion-ready');
      });
    });
    root.dataset.bannannaFitReady = 'true';
  }

  function initFitOutcomes(root) {
    if (!root || root.dataset.bannannaFitOutcomesReady === 'true') return;

    const panels = Array.from(root.querySelectorAll('[data-fit-outcome-panel]'));
    const status = root.querySelector('[data-fit-outcome-status]');
    if (!panels.length) return;
    let outcomeSwapToken = 0;

    function getSafeIndex(index) {
      const requestedIndex = String(index || '0');
      const hasMatch = panels.some((panel) => panel.dataset.fitOutcomePanel === requestedIndex);
      return hasMatch ? requestedIndex : panels[0].dataset.fitOutcomePanel;
    }

    function setActivePanel(index) {
      const activeIndex = getSafeIndex(index);
      const activePanel = panels.find((panel) => panel.dataset.fitOutcomePanel === activeIndex) || panels[0];
      const swapToken = ++outcomeSwapToken;

      decodePanelImages(activePanel).then(() => {
        if (swapToken !== outcomeSwapToken) return;

        panels.forEach((panel) => {
          const isActive = panel === activePanel;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          setFocusableState(panel, isActive);
        });

        if (status && activePanel) {
          status.textContent = `${getPanelName(activePanel)} outcomes shown.`;
        }
      });
    }

    document.addEventListener('bannanna:fit-change', (event) => {
      setActivePanel(event.detail && event.detail.index);
    });

    const activeTrigger = document.querySelector('[data-fit-trigger].is-active');
    setActivePanel(activeTrigger ? activeTrigger.dataset.fitTrigger : panels[0].dataset.fitOutcomePanel);
    root.dataset.bannannaFitOutcomesReady = 'true';
  }

  function initAll() {
    document.querySelectorAll('[data-bannanna-fit-module]').forEach(initFitDiscovery);
    document.querySelectorAll('[data-bannanna-fit-outcomes]').forEach(initFitOutcomes);
  }

  window.BannannaHomeInteractions = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
